/* eslint-disable array-bracket-newline */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useAccount } from 'wagmi';
import {
  DragDropContext,
  Droppable,
  Draggable,
  OnDragEndResponder,
} from 'react-beautiful-dnd';
import { IFetchTickersPayload, ITicker, enums } from '@kattana-io/common';
import Text from '../../atoms/Text';
import Loader from '../common/Loader';
import AlertsView from './AlertsView';
import WatchlistRowNotTicker from './WatchlistRowNotTicker';
import WatchlistRow, { ExchangeStatus } from './WatchlistRow';
import WatchlistTabs from './WatchlistTabs';
import useGetWatchlistItems from './hooks/useGetWatchlistItems';
import useTrendingItems from './hooks/useTrendingItems';
import {
  actionAddWatchlist,
  actionRemoveWatchlist,
  actionSetWatchlistTab,
  actionUpdateWatchlist,
} from '../../actions/watchlist';
import { actionShowModal } from '../../actions/modal';
import { ModalTypes } from '../../reducers/modal';
import { IStore } from '../../reducers';
import { KattanaAPI } from '../../store/createStore';
import { switchCurrentTicker } from '../../Authlogic';
import {
  UpdateWatchlistItemPayload,
  WatchlistTab,
} from '../../reducers/watchlist';
import WatchlistWarning from './WatchlistWarning';
import PairStats from './CEX/PairStats';
import { COMPETITION_ENABLED } from '../../helpers/constants';
import { getWatchedPriceAlerts } from '../../selectors/priceAlerts';

import './styles.scss';

const LS_HIDE_COMPETITION_NOTICE_KEY = 'kattana-simple.competition';
const DESKTOP_CEX_WARNING_WIDTH = '246px';
export const CEX_WARNING_TEXT =
  'To ensure proper functionality, please connect your API key; without it, the Binance widget will not work.';
const PRICE_ALERTS_WARNING_TEXT =
  'You reached the max amount of price alerts for free version - upgrade to Premium to get unlimited feature';

const WATCHLIST_TABS = COMPETITION_ENABLED
  ? [WatchlistTab.Gainers, WatchlistTab.TVL, WatchlistTab.Competition]
  : [WatchlistTab.Gainers, WatchlistTab.TVL];

interface IProps {
  visible: boolean;
}

const Watchlist = ({ visible }: IProps) => {
  const trending_items = useSelector(
    (state: IStore) => state.registry.trending
  );
  const watchedTickers = useSelector(
    (state: IStore) => state.watchlist.watched
  );
  const watchlistItems = useSelector(
    (state: IStore) => state.watchlist.tickers
  );
  const binance = useSelector(
    (state: IStore) => state.cex_trading.exchanges.binance?.enable
  );
  const watchlistCompetition = useSelector(
    (state: IStore) => state.watchlist.competition
  );
  const tab = useSelector((state: IStore) => state.watchlist.tab);
  const loading = useSelector((state: IStore) => state.watchlist.loading);
  const current = useSelector((state: IStore) => state.current);
  const priceAlertsLimit = useSelector(
    (state: IStore) => state.features.features.priceAlerts
  );
  const priceAlertsNumber = useSelector(getWatchedPriceAlerts);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchedData, setSearchedData] = useState<ITicker[] | null>([]);
  const { address: account } = useAccount();
  const hasAccount = !!account;
  const [search, setSearch] = useState('');
  const { trending_items_ids, trending_items_no_ids } =
    useTrendingItems(trending_items);

  // trending tickers
  const trending = useGetWatchlistItems(trending_items_ids.join(','));

  const collection = useMemo(
    () =>
      watchlistItems.filter(
        (item) =>
          item?.name.toLowerCase().includes(search.toLowerCase()) ||
          item?.address.toLowerCase().includes(search.toLowerCase())
      ),
    [watchlistItems, search]
  );

  const onAccountRequired = useCallback(
    () => dispatch(actionShowModal(ModalTypes.connect, {})),
    [dispatch]
  );

  const onSwitch = useCallback((payload: any) => {
    switchCurrentTicker(payload);
  }, []);

  const onRemove = useCallback(
    (index: number, callback: () => void) =>
      dispatch(actionRemoveWatchlist(index, callback)),
    [dispatch]
  );

  const onAdd = useCallback(
    (item: ITicker, tag?: enums.Tag, callback?: () => void) =>
      dispatch(actionAddWatchlist(item, tag, callback)),
    [dispatch]
  );

  const onUpdate = useCallback(
    (id: number, payload: UpdateWatchlistItemPayload) => {
      dispatch(actionUpdateWatchlist(id, payload));
    },
    [dispatch]
  );

  /**
   * Connect API key
   * @param side
   */
  const onConnectExchange = useCallback(() => {
    if (account) {
      return dispatch(
        actionShowModal(ModalTypes.connectCex, { exchange: 'binance' })
      );
    }
    dispatch(actionShowModal(ModalTypes.connect, {}));
  }, [account, dispatch]);

  /**
   * Show premium modal
   * @param side
   */
  const onSubscribeToPremium = useCallback(() => {
    navigate('/subscription');
  }, [navigate]);

  const handleOnDragEnd = useCallback<OnDragEndResponder>(
    ({ source, destination }) => {
      if (!destination || source.index === destination.index) {
        return;
      }
      const draggedItem = collection[source.index];

      onUpdate(draggedItem.id, {
        old: source.index,
        new: destination.index,
      });
    },
    [collection, onUpdate]
  );

  const searchForTickets = async (controller: AbortController) => {
    const payload: IFetchTickersPayload = {
      searchRaw: search,
      sort: {
        name: 'liquidity',
        order: 'DESC',
      },
    };
    const data = await KattanaAPI.tickers(payload, {
      signal: controller.signal,
    });
    setSearchedData(data);
  };

  useEffect(() => {
    const controller = new AbortController();
    if (search) {
      searchForTickets(controller);
    } else {
      setSearchedData(null);
    }
    return () => controller.abort();
  }, [search]);

  const topThreeTickets = useMemo(() => {
    const excludes: any = [
      ...watchedTickers,
      ...watchlistItems,
      current,
      ...trending.items,
    ].reduce((cons, el) => ({ ...cons, [el.id]: el.name }), {});

    return searchedData?.filter((item) => !excludes[item.id]).slice(0, 3);
  }, [watchedTickers, watchlistItems, current, trending.items, searchedData]);

  const isCurrentPresent = [...trending.items, ...watchlistItems].some(
    (el) => el.id === current.id
  );

  const cexWarningCondition = !binance && tab === WatchlistTab.Competition;
  const priceAlertsWarningCondition =
    tab !== WatchlistTab.Competition &&
    priceAlertsLimit &&
    priceAlertsNumber.length >= priceAlertsLimit;

  const renderContent = () => {
    if (loading) {
      return (
        <div className='centrify'>
          <Loader />
        </div>
      );
    }
    if (tab === WatchlistTab.Competition) {
      return (
        <>
          {watchlistCompetition.map((item, index) => (
            <div
              key={item.id}
              className='watchlist__cex'
              onClick={() => onSwitch(item)}
            >
              <WatchlistRow
                item={item as any}
                active={false}
                account={hasAccount}
                onAccountRequired={onAccountRequired}
                isFirst={index === 0}
                exchangeStatus={
                  binance ? ExchangeStatus.enabled : ExchangeStatus.disabled
                }
                multiplier={item.multiplier_global * item.multiplier_private}
              />
              <PairStats {...item} />
              {index !== watchlistCompetition.length - 1 && (
                <div className='watchlist__cex__divider' />
              )}
            </div>
          ))}
        </>
      );
    }
    return (
      <>
        {!isCurrentPresent && tab === WatchlistTab.Star && (
          <WatchlistRow
            actionSwitchCurrent={onSwitch}
            actionAddWatchlist={onAdd}
            item={current}
            account={hasAccount}
            onAccountRequired={onAccountRequired}
            isFirst
            active
          />
        )}
        {trending_items_no_ids.map((item) => (
          <WatchlistRowNotTicker key={item.id} item={item} />
        ))}
        {trending.items.map((item, index) => (
          <WatchlistRow
            actionSwitchCurrent={onSwitch}
            key={item.id}
            item={item}
            active={current.id === item.id}
            account={hasAccount}
            onAccountRequired={onAccountRequired}
            isFirst={isCurrentPresent && index === 0}
            isTrending
          />
        ))}
        {collection.map((item, index) => {
          const isInWatchList =
            tab === WatchlistTab.Star ||
            watchedTickers.some((ticker) => ticker.id === item.id);
          return (
            <Draggable
              key={item.id}
              draggableId={item.id.toString()}
              index={index}
              isDragDisabled={tab !== WatchlistTab.Star}
            >
              {(provided) => (
                <div
                  className='watchlist__draggable'
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                >
                  <WatchlistRow
                    actionSwitchCurrent={onSwitch}
                    actionRemoveWatchlist={isInWatchList ? onRemove : undefined}
                    actionAddWatchlist={isInWatchList ? undefined : onAdd}
                    actionUpdateWatchlist={isInWatchList ? onUpdate : undefined}
                    item={item}
                    active={current.id === item.id}
                    account={hasAccount}
                    onAccountRequired={onAccountRequired}
                    isFirst={
                      isCurrentPresent &&
                      trending.items.length === 0 &&
                      index === 0
                    }
                  />
                </div>
              )}
            </Draggable>
          );
        })}
      </>
    );
  };

  return (
    <div className={`watchlist ${visible ? '' : 'hide'}`}>
      <div className='container'>
        <WatchlistTabs
          tabs={WATCHLIST_TABS}
          starTab={WatchlistTab.Star}
          activeTab={tab}
          setTab={(tab: WatchlistTab) => dispatch(actionSetWatchlistTab(tab))}
          noticeTab={WatchlistTab.Competition}
          noticeKey={LS_HIDE_COMPETITION_NOTICE_KEY}
        />
        <input
          type='search'
          className='search__input text--12 text--grey-600 text--weight-600'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder='Search in Watchlist...'
        />
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <Droppable droppableId='watchlist-items'>
            {(provided, snapshot) => (
              <div
                className={`items_container ${
                  snapshot.isDraggingOver ? 'items_container__overlay' : ''
                }`}
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {renderContent()}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        {!!topThreeTickets?.length && (
          <div>
            <div className='watchlist__search_divider'>
              <Text
                size={14}
                className='watchlist__search_divider__search text--grey-400'
              >
                search
              </Text>
            </div>
            {topThreeTickets.map((item) => (
              <WatchlistRow
                key={item.id}
                actionSwitchCurrent={onSwitch}
                actionAddWatchlist={onAdd}
                item={item}
                active={current.id === item.id}
                account={hasAccount}
                onAccountRequired={onAccountRequired}
              />
            ))}
          </div>
        )}
        {cexWarningCondition && (
          <WatchlistWarning
            width={DESKTOP_CEX_WARNING_WIDTH}
            absolute={!topThreeTickets?.length}
            text={CEX_WARNING_TEXT}
            buttonText='Connect API Key'
            onSubmit={onConnectExchange}
          />
        )}
        {priceAlertsWarningCondition && (
          <WatchlistWarning
            width={DESKTOP_CEX_WARNING_WIDTH}
            text={PRICE_ALERTS_WARNING_TEXT}
            buttonText='Upgrade'
            onSubmit={onSubscribeToPremium}
          />
        )}
      </div>
      <AlertsView />
    </div>
  );
};

export default Watchlist;
